import React from 'react';
import { Box, Container } from '@mui/material';
import { AppHeader } from './AppHeader';
import { Breadcrumbs, Breadcrumb } from './Breadcrumbs';

/**
 * AppLayout Component
 *
 * Persistent layout wrapper for all authenticated pages
 *
 * Features:
 * - Sticky header with navigation and search
 * - Breadcrumb navigation below header
 * - Responsive container for main content
 * - Consistent spacing and layout
 *
 * @component
 * @param {AppLayoutProps} props - Component props
 * @returns {JSX.Element} Application layout wrapper
 */
interface AppLayoutProps {
  children: React.ReactNode;
  breadcrumbs?: Breadcrumb[];
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  disablePadding?: boolean;
}

export const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  breadcrumbs,
  maxWidth = 'lg',
  disablePadding = false,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: 'background.default',
      }}
    >
      {/* Header - Fixed at top */}
      <AppHeader />

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: { xs: 8, sm: 9 }, // Offset for fixed header (64px mobile, 72px desktop)
          pb: 4,
        }}
      >
        {/* Breadcrumbs */}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <Container maxWidth={maxWidth}>
            <Box sx={{ py: 2 }}>
              <Breadcrumbs breadcrumbs={breadcrumbs} />
            </Box>
          </Container>
        )}

        {/* Page Content */}
        <Container
          maxWidth={maxWidth}
          disableGutters={disablePadding}
          sx={{
            px: disablePadding ? 0 : { xs: 2, sm: 3 },
          }}
        >
          {children}
        </Container>
      </Box>
    </Box>
  );
};
